import React, { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import {
  Shuffle, Search, DollarSign, Package, ArrowUpDown, Download, History, EyeOff,
  ChevronDown, ChevronUp, Handshake, UserPlus, Trophy, SkipForward, ExternalLink,
  CheckCircle, XCircle, Loader2, AlertTriangle, Zap, FileSpreadsheet, CircleDot,
} from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';
import InfoTip from '@/components/InfoTip';
import QuickAssignModal from '@/components/QuickAssignModal';
import DateRangeSelect from '@/components/DateRangeSelect';
import SiteDeptFilter from '@/components/SiteDeptFilter';
import { useCurrency } from '@/context/CurrencyContext';
import { useNavigate } from 'react-router-dom';

/* ─── CA-2: Data Quality Badge ─── */
const DataQualityBadge = ({ quality }) => {
  if (quality === 'verified') return <Badge className="bg-green-100 text-green-800 text-[10px]"><CircleDot className="h-2.5 w-2.5 mr-0.5" />Verified</Badge>;
  if (quality === 'estimated') return <Badge className="bg-amber-100 text-amber-800 text-[10px]"><CircleDot className="h-2.5 w-2.5 mr-0.5" />Estimated</Badge>;
  return <Badge className="bg-red-100 text-red-800 text-[10px]"><CircleDot className="h-2.5 w-2.5 mr-0.5" />N/A</Badge>;
};

/* ─── CA-2: Price display ─── */
const PriceDisplay = ({ price, quality }) => {
  if (!price || price <= 0 || quality === 'unavailable')
    return <span className="text-red-500 text-xs italic">Request Quote</span>;
  return <span>${price.toFixed(2)}</span>;
};

const SmartAlternates = () => {
  const { format } = useCurrency();
  const navigate = useNavigate();
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCtx, setAssignCtx] = useState({});

  const [alternates, setAlternates] = useState([]);
  const [summary, setSummary] = useState({});
  const [filterOptions, setFilterOptions] = useState({ bom_names: [], departments: [], countries: [] });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('value_impact');
  const [filterBom, setFilterBom] = useState('all');
  const [filterDept, setFilterDept] = useState('all');
  const [filterCountry, setFilterCountry] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [filterSite, setFilterSite] = useState('All Sites');
  const [expandedRow, setExpandedRow] = useState(null);
  const [altDetails, setAltDetails] = useState({});
  const [altLoading, setAltLoading] = useState(null);

  // Ignore
  const [ignoreOpen, setIgnoreOpen] = useState(false);
  const [ignoreTarget, setIgnoreTarget] = useState(null);
  const [ignoreReason, setIgnoreReason] = useState('');
  const [ignoredList, setIgnoredList] = useState([]);
  const [ignoredLoading, setIgnoredLoading] = useState(false);

  // CA-3: Approve modal
  const [approveTarget, setApproveTarget] = useState(null);
  const [switchingCost, setSwitchingCost] = useState('0');
  const [approving, setApproving] = useState(false);

  // CA-4: Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);

  // Export loading
  const [exporting, setExporting] = useState(false);

  const fetchAlternates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort_by: sortBy });
      if (filterBom !== 'all') params.append('bom_name', filterBom);
      if (filterDept !== 'all') params.append('department', filterDept);
      if (filterCountry !== 'all') params.append('country', filterCountry);
      const res = await api.get(`/alternates/smart?${params}`);
      setAlternates(res.data.alternates || []);
      setSummary(res.data.summary || {});
      setFilterOptions(res.data.filters || {});
    } catch { toast.error('Failed to load alternates'); }
    finally { setLoading(false); }
  }, [sortBy, filterBom, filterDept, filterCountry]);

  useEffect(() => { fetchAlternates(); }, [fetchAlternates]);

  const fetchIgnored = async () => {
    setIgnoredLoading(true);
    try {
      const res = await api.get('/alternates/ignored');
      setIgnoredList(res.data.ignored || []);
    } catch { toast.error('Failed to load ignored list'); }
    finally { setIgnoredLoading(false); }
  };

  const handleIgnore = async () => {
    if (!ignoreTarget) return;
    try {
      await api.post('/alternates/ignore', { component_id: ignoreTarget.component_id, alternate_mpn: ignoreTarget.best_alternate_mpn, original_mpn: ignoreTarget.mpn, reason: ignoreReason });
      toast.success('Alternate ignored');
      setIgnoreOpen(false);
      setIgnoreReason('');
      fetchAlternates();
    } catch { toast.error('Failed to ignore'); }
  };

  const fetchComponentAlternates = async (componentId) => {
    if (altDetails[componentId]) {
      setExpandedRow(expandedRow === componentId ? null : componentId);
      return;
    }
    setAltLoading(componentId);
    try {
      const res = await api.get(`/alternates/component/${componentId}`);
      setAltDetails(prev => ({ ...prev, [componentId]: res.data }));
      setExpandedRow(componentId);
    } catch { toast.error('Failed to load detail'); }
    finally { setAltLoading(null); }
  };

  // CA-3: Approve alternate
  const handleApprove = async () => {
    if (!approveTarget) return;
    setApproving(true);
    try {
      const res = await api.post('/alternates/approve', {
        component_id: approveTarget.component_id,
        alternate_mpn: approveTarget.best_alternate_mpn,
        original_mpn: approveTarget.mpn,
        bom_id: approveTarget.bom_id,
        savings_value: approveTarget.savings_value,
        switching_cost: parseFloat(switchingCost) || 0,
      });
      toast.success(res.data.message);
      setApproveTarget(null);
      setSwitchingCost('0');
      fetchAlternates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
    finally { setApproving(false); }
  };

  // CA-4: Bulk approve
  const handleBulkApprove = async () => {
    if (selected.size === 0) return;
    setBulkApproving(true);
    try {
      const items = alternates.filter(a => selected.has(a.component_id)).map(a => ({
        component_id: a.component_id, original_mpn: a.mpn,
        alternate_mpn: a.best_alternate_mpn, bom_id: a.bom_id,
        savings_value: a.savings_value, switching_cost: a.switching_cost || 0,
      }));
      const res = await api.post('/alternates/bulk-approve', { items });
      toast.success(res.data.message);
      setSelected(new Set());
      fetchAlternates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk approve failed'); }
    finally { setBulkApproving(false); }
  };

  // CA-4: Excel export
  const handleExport = async () => {
    setExporting(true);
    try {
      const rows = filtered.map(a => [
        a.mpn, a.category, a.bom_name, a.best_alternate_mpn,
        a.current_price, a.best_alternate_price, a.savings_percent,
        a.savings_value, a.savings_type, a.switching_cost, a.net_savings,
        a.data_quality, a.approval_status, a.department,
      ].join(','));
      const header = 'MPN,Category,BOM,Best Alt,Your Price,Alt Price,Savings%,Savings$,Type,Switching Cost,Net Savings,Data Quality,Status,Dept';
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'smart-alternates-export.csv'; a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(a => a.component_id)));
  };

  const filtered = alternates.filter(a => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return a.mpn?.toLowerCase().includes(q) || a.best_alternate_mpn?.toLowerCase().includes(q) || a.bom_name?.toLowerCase().includes(q);
  });

  return (
    <Layout title="Smart Alternates" subtitle="AI-powered FFF alternate suggestions with approval workflow" breadcrumbs={[{ label: 'Smart Alternates' }]}>
      <div className="space-y-6" data-testid="smart-alternates-page">

        {/* KPI Summary — CA-1: weighted median, CA-5: bifurcated */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="alternates-summary">
          <Card className="bg-blue-50 border-blue-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-blue-600 flex items-center gap-1">FFF Alternates <InfoTip text="Total FFF alternates found across all components." /></p>
              <p className="text-2xl font-bold text-blue-700">{summary.total_fff_alternates || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-green-600 flex items-center gap-1">Total Savings <InfoTip text="Gross savings before switching costs." /></p>
              <p className="text-2xl font-bold text-green-700">${(summary.total_savings || 0).toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-emerald-600 flex items-center gap-1">Avg Savings <InfoTip text="Weighted median savings %. Uses weighted median (not mean) to prevent outlier distortion." /></p>
              <p className="text-2xl font-bold text-emerald-700">{summary.weighted_median_savings || 0}%</p>
              <p className="text-[10px] text-gray-500">weighted median</p>
            </CardContent>
          </Card>
          {/* CA-5: Bifurcated */}
          <Card className="bg-cyan-50 border-cyan-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-cyan-600 flex items-center gap-1">Same-Make <InfoTip text="Savings from better pricing on the SAME MPN from different distributors." /></p>
              <p className="text-2xl font-bold text-cyan-700">${(summary.same_make_savings || 0).toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-purple-600 flex items-center gap-1">FFF Savings <InfoTip text="Savings from switching to a different MPN (form-fit-function alternate)." /></p>
              <p className="text-2xl font-bold text-purple-700">${(summary.fff_savings || 0).toLocaleString()}</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-xs text-amber-600 flex items-center gap-1">Net (after costs) <InfoTip text="Total savings minus estimated switching costs (qualification testing, tooling changes)." /></p>
              <p className="text-2xl font-bold text-amber-700">${(summary.total_net_savings || 0).toLocaleString()}</p>
              <p className="text-[10px] text-red-500">-${(summary.total_switching_cost || 0).toLocaleString()} switching</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="alternates" className="w-full" onValueChange={v => { if (v === 'ignored') fetchIgnored(); }}>
          <TabsList className="grid w-full grid-cols-3 max-w-lg">
            <TabsTrigger value="alternates">Alternates</TabsTrigger>
            <TabsTrigger value="approved" data-testid="approved-tab"><CheckCircle className="h-3.5 w-3.5 mr-1" />Approved</TabsTrigger>
            <TabsTrigger value="ignored" data-testid="ignored-tab"><History className="h-3.5 w-3.5 mr-1" />Ignored</TabsTrigger>
          </TabsList>

          <TabsContent value="alternates" className="mt-4">
            {/* Filters + Bulk Actions */}
            <Card className="bg-white border-gray-200 shadow-sm mb-4">
              <CardContent className="pt-3 pb-3">
                <div className="flex gap-3 flex-wrap items-center">
                  <div className="relative flex-1 min-w-[180px]">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input placeholder="Search MPN, alternate, BOM..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9" data-testid="search-alternates" />
                  </div>
                  <Select value={filterBom} onValueChange={setFilterBom}>
                    <SelectTrigger className="w-36 h-9" data-testid="filter-bom"><SelectValue placeholder="BOM" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All BOMs</SelectItem>
                      {filterOptions.bom_names?.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <SiteDeptFilter site={filterSite} onSiteChange={setFilterSite} dept={filterDept} onDeptChange={setFilterDept} />
                  <DateRangeSelect value={dateRange} onChange={setDateRange} testId="alt-date-range" />
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-36 h-9" data-testid="sort-by"><ArrowUpDown className="h-3 w-3 mr-1" /><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="value_impact">Value Impact</SelectItem>
                      <SelectItem value="savings_percent">Savings %</SelectItem>
                      <SelectItem value="match_score">Match Score</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" className="h-9 gap-1" onClick={handleExport} disabled={exporting} data-testid="export-csv-btn">
                    {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} CSV
                  </Button>
                  {selected.size > 0 && (
                    <Button className="h-9 bg-green-600 hover:bg-green-700 text-white gap-1" onClick={handleBulkApprove} disabled={bulkApproving} data-testid="bulk-approve-btn">
                      {bulkApproving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                      Approve {selected.size} Selected
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Table */}
            {loading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="h-10 w-10 animate-spin text-green-600" /></div>
            ) : (
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardContent className="pt-4">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="w-10">
                            <Checkbox checked={selected.size === filtered.length && filtered.length > 0} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" />
                          </TableHead>
                          <TableHead>MPN</TableHead>
                          <TableHead>Best Alternate</TableHead>
                          <TableHead>Your Price</TableHead>
                          <TableHead>
                            <span className="flex items-center gap-0.5">Savings <InfoTip text="Savings % = (Your Price - Alt Price) / Your Price x 100" /></span>
                          </TableHead>
                          <TableHead><span className="flex items-center gap-0.5">Type <InfoTip text="same_make = better price on same MPN. fff = different MPN alternate." /></span></TableHead>
                          <TableHead><span className="flex items-center gap-0.5">Quality <InfoTip text="Green = verified pricing. Amber = estimated/spot. Red = unavailable, request quote." /></span></TableHead>
                          <TableHead>FFF</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Detail</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filtered.length === 0 ? (
                          <TableRow><TableCell colSpan={11} className="text-center py-8 text-gray-500">No alternates found.</TableCell></TableRow>
                        ) : filtered.map((alt, idx) => (
                          <React.Fragment key={idx}>
                            <TableRow className={`hover:bg-gray-50 ${alt.approval_status === 'approved' ? 'bg-green-50/30' : ''}`} data-testid={`alternate-row-${idx}`}>
                              <TableCell>
                                <Checkbox checked={selected.has(alt.component_id)} onCheckedChange={() => toggleSelect(alt.component_id)} />
                              </TableCell>
                              <TableCell>
                                <button onClick={() => navigate(`/component-intelligence/${encodeURIComponent(alt.mpn)}`)} className="font-mono text-sm font-medium text-gray-900 hover:text-green-700">{alt.mpn}</button>
                                <p className="text-[10px] text-gray-400">{alt.bom_name}</p>
                              </TableCell>
                              <TableCell className="font-mono text-sm font-medium text-green-700">{alt.best_alternate_mpn}</TableCell>
                              <TableCell className="text-sm">{alt.current_price > 0 ? `$${alt.current_price.toFixed(2)}` : <span className="text-red-500 text-xs italic">N/A</span>}</TableCell>
                              <TableCell>
                                <PriceDisplay price={alt.best_alternate_price} quality={alt.data_quality} />
                                {alt.savings_percent > 0 && <Badge className="bg-green-100 text-green-800 ml-1 text-[10px]">{alt.savings_percent}%</Badge>}
                              </TableCell>
                              <TableCell>
                                <Badge className={alt.savings_type === 'fff' ? 'bg-purple-100 text-purple-800 text-[10px]' : 'bg-cyan-100 text-cyan-800 text-[10px]'}>
                                  {alt.savings_type === 'fff' ? 'FFF' : 'Same-Make'}
                                </Badge>
                              </TableCell>
                              <TableCell><DataQualityBadge quality={alt.data_quality} /></TableCell>
                              <TableCell className="text-center"><Badge className="bg-blue-100 text-blue-800">{alt.fff_count}</Badge></TableCell>
                              <TableCell>
                                {alt.approval_status === 'approved' ? (
                                  <Badge className="bg-green-100 text-green-800 text-[10px]"><CheckCircle className="h-2.5 w-2.5 mr-0.5" />Approved</Badge>
                                ) : (
                                  <Badge variant="outline" className="text-[10px]">Pending</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <Button size="sm" variant="outline" className={`h-7 px-2 text-xs ${expandedRow === alt.component_id ? 'bg-blue-50 border-blue-300' : ''}`} onClick={() => fetchComponentAlternates(alt.component_id)} data-testid={`view-detail-${idx}`}>
                                  {altLoading === alt.component_id ? '...' : expandedRow === alt.component_id ? <><ChevronUp className="h-3 w-3 mr-1" />Hide</> : <><ExternalLink className="h-3 w-3 mr-1" />Detail</>}
                                </Button>
                              </TableCell>
                              <TableCell>
                                <div className="flex gap-1">
                                  {alt.approval_status !== 'approved' && (
                                    <Button size="sm" className="h-7 px-2 text-xs bg-green-600 hover:bg-green-700 text-white" onClick={() => setApproveTarget(alt)} data-testid={`approve-btn-${idx}`}>
                                      <Zap className="h-3 w-3 mr-0.5" /> Approve
                                    </Button>
                                  )}
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button size="sm" variant="outline" className="h-7 px-1.5"><ChevronDown className="h-3 w-3" /></Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-44">
                                      <DropdownMenuItem onClick={() => { setAssignCtx({ mpn: alt.mpn, sourceModule: 'Smart Alternates' }); setAssignOpen(true); }}>
                                        <UserPlus className="h-3.5 w-3.5 mr-2 text-blue-600" /> Assign
                                      </DropdownMenuItem>
                                      <DropdownMenuItem onClick={() => { setIgnoreTarget(alt); setIgnoreOpen(true); }}>
                                        <EyeOff className="h-3.5 w-3.5 mr-2 text-gray-500" /> Skip
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              </TableCell>
                            </TableRow>
                            {/* Expanded Detail */}
                            {expandedRow === alt.component_id && altDetails[alt.component_id] && (
                              <TableRow data-testid={`detail-expanded-${idx}`}>
                                <TableCell colSpan={11} className="bg-blue-50 border-l-4 border-l-blue-400 p-4">
                                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                    <Shuffle className="h-4 w-4 text-blue-600" /> Alternates for {alt.mpn}
                                    <Badge className="bg-blue-100 text-blue-800 text-xs">{altDetails[alt.component_id].fff_alternates?.length || 0} FFF</Badge>
                                    <Badge variant="outline" className="text-xs">{altDetails[alt.component_id].other_alternates?.length || 0} Other</Badge>
                                  </h4>
                                  <Table>
                                    <TableHeader>
                                      <TableRow className="bg-white/80">
                                        <TableHead>Alternate MPN</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Manufacturer</TableHead>
                                        <TableHead>Price</TableHead>
                                        <TableHead>Quality</TableHead>
                                        <TableHead>Best Rate</TableHead>
                                        <TableHead>Risk</TableHead>
                                        <TableHead>Datasheet</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {[...(altDetails[alt.component_id].fff_alternates || []), ...(altDetails[alt.component_id].other_alternates || [])].map((a, ai) => (
                                        <TableRow key={ai} className={a.is_fff ? 'bg-green-50/50' : ''}>
                                          <TableCell className="font-mono text-sm font-medium">{a.mpn}</TableCell>
                                          <TableCell><Badge className={a.is_fff ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}>{a.match_type}</Badge></TableCell>
                                          <TableCell className="text-sm">{a.manufacturer}</TableCell>
                                          <TableCell><PriceDisplay price={a.price} quality={a.data_quality} /></TableCell>
                                          <TableCell><DataQualityBadge quality={a.data_quality} /></TableCell>
                                          <TableCell className="font-semibold text-green-700"><PriceDisplay price={a.best_rate} quality={a.data_quality} /></TableCell>
                                          <TableCell>
                                            <Badge className={a.risk_level === 'Low' ? 'bg-green-100 text-green-800' : a.risk_level === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>
                                              {a.risk_level || 'Low'}
                                            </Badge>
                                          </TableCell>
                                          <TableCell>
                                            <a href={a.datasheet_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs flex items-center gap-1">
                                              <ExternalLink className="h-3 w-3" /> Sheet
                                            </a>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="mt-3 text-sm text-gray-500 text-right">Showing {filtered.length} of {alternates.length}</div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Approved Tab */}
          <TabsContent value="approved" className="mt-4">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader><CardTitle className="text-sm flex items-center gap-2"><CheckCircle className="h-4 w-4 text-green-600" /> Approved Replacements</CardTitle></CardHeader>
              <CardContent>
                {alternates.filter(a => a.approval_status === 'approved').length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">No approved alternates yet. Use the "Approve" button to start.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Original MPN</TableHead>
                        <TableHead>Replacement MPN</TableHead>
                        <TableHead>BOM</TableHead>
                        <TableHead>Savings</TableHead>
                        <TableHead>Switching Cost</TableHead>
                        <TableHead>Net Savings</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {alternates.filter(a => a.approval_status === 'approved').map((a, i) => (
                        <TableRow key={i} className="bg-green-50/30" data-testid={`approved-row-${i}`}>
                          <TableCell className="font-mono text-sm">{a.mpn}</TableCell>
                          <TableCell className="font-mono text-sm font-medium text-green-700">{a.best_alternate_mpn}</TableCell>
                          <TableCell><Badge variant="outline" className="text-xs">{a.bom_name}</Badge></TableCell>
                          <TableCell className="font-bold text-green-700">${a.savings_value.toFixed(2)}</TableCell>
                          <TableCell className="text-red-600">${(a.switching_cost || 0).toFixed(2)}</TableCell>
                          <TableCell className="font-bold text-emerald-700">${a.net_savings.toFixed(2)}</TableCell>
                          <TableCell><Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-0.5" />Approved</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ignored Tab */}
          <TabsContent value="ignored" className="mt-4">
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader><CardTitle className="text-sm flex items-center gap-2"><History className="h-4 w-4 text-gray-500" /> Skipped Alternates</CardTitle></CardHeader>
              <CardContent>
                {ignoredLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-gray-400" /></div>
                ) : ignoredList.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8" data-testid="no-ignored-message">No skipped alternates.</p>
                ) : (
                  <Table>
                    <TableHeader><TableRow className="bg-gray-50"><TableHead>Original MPN</TableHead><TableHead>Alternate MPN</TableHead><TableHead>Reason</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {ignoredList.map((item, idx) => (
                        <TableRow key={idx} data-testid={`ignored-row-${idx}`}>
                          <TableCell className="font-mono text-sm">{item.original_mpn || item.component_id}</TableCell>
                          <TableCell className="font-mono text-sm font-medium">{item.alternate_mpn}</TableCell>
                          <TableCell className="text-sm text-gray-600">{item.reason || 'No reason'}</TableCell>
                          <TableCell className="text-sm text-gray-500">{new Date(item.ignored_at).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Approve Dialog (CA-3) */}
      <Dialog open={!!approveTarget} onOpenChange={() => setApproveTarget(null)}>
        <DialogContent className="max-w-md" data-testid="approve-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Zap className="h-5 w-5 text-green-600" /> Approve & Replace</DialogTitle></DialogHeader>
          {approveTarget && (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-gray-50 rounded-lg border space-y-1">
                <div className="flex justify-between text-sm"><span className="text-gray-500">Original:</span><span className="font-mono font-semibold">{approveTarget.mpn}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">Replace with:</span><span className="font-mono font-semibold text-green-700">{approveTarget.best_alternate_mpn}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">BOM:</span><span>{approveTarget.bom_name}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">Gross Savings:</span><span className="font-bold text-green-600">${approveTarget.savings_value?.toFixed(2)}</span></div>
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  Estimated Switching Cost ($)
                  <InfoTip text="Cost of qualification testing, tooling changes, and other expenses when switching to a new component." />
                </Label>
                <Input type="number" step="0.01" value={switchingCost} onChange={e => setSwitchingCost(e.target.value)} className="mt-1" data-testid="switching-cost-input" />
                <p className="text-xs text-gray-500 mt-1">Net Savings: <strong className="text-emerald-700">${(approveTarget.savings_value - (parseFloat(switchingCost) || 0)).toFixed(2)}</strong></p>
              </div>
              <div className="p-2 bg-blue-50 rounded border border-blue-200 text-xs text-blue-700">
                <AlertTriangle className="h-3.5 w-3.5 inline mr-1" /> This will create a BOM change request and an Action Center item for procurement.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveTarget(null)}>Cancel</Button>
            <Button onClick={handleApprove} disabled={approving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-approve-btn">
              {approving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Approving...</> : <><CheckCircle className="h-4 w-4 mr-1" /> Approve & Replace</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ignore Dialog */}
      <Dialog open={ignoreOpen} onOpenChange={setIgnoreOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><EyeOff className="h-5 w-5 text-gray-500" /> Skip Suggestion</DialogTitle></DialogHeader>
          {ignoreTarget && (
            <div className="space-y-4 py-2">
              <div className="bg-gray-50 rounded p-3 text-sm">
                <p><strong>Component:</strong> {ignoreTarget.mpn}</p>
                <p><strong>Alternate:</strong> {ignoreTarget.best_alternate_mpn}</p>
              </div>
              <div><Label>Reason (optional)</Label><Textarea value={ignoreReason} onChange={e => setIgnoreReason(e.target.value)} placeholder="e.g., Already evaluated..." className="mt-1" rows={3} data-testid="ignore-reason" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIgnoreOpen(false)}>Cancel</Button>
            <Button onClick={handleIgnore} className="bg-gray-700 hover:bg-gray-800 text-white" data-testid="confirm-ignore-btn">Skip</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuickAssignModal open={assignOpen} onClose={() => setAssignOpen(false)} context={assignCtx} />
    </Layout>
  );
};

export default SmartAlternates;
